import { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../../api";

// Conteggi di log Critical/High per LECS nel periodo selezionato, indicizzati
// per matricola. Alimenta le colonne eventi della tabella "My LECS".

const toNumberSafe = (v) => {
  if (v === null || v === undefined) return null;

  // Mongo Extended JSON
  if (typeof v === "object") {
    if (v && "$numberInt" in v) return Number(v.$numberInt);
    if (v && "$numberLong" in v) return Number(v.$numberLong);
    if (v && "$numberDouble" in v) return Number(v.$numberDouble);
  }

  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const unwrapApiArray = (res) => {
  const d1 = res?.data;
  if (Array.isArray(d1)) return d1;

  const d2 = res?.data?.data;
  if (Array.isArray(d2)) return d2;

  return [];
};

const findCountDeep = (obj) => {
  if (!obj || typeof obj !== "object") return null;

  const preferredKeys = [
    "count", "conta", "total", "totale", "logs", "logCount", "logsCount",
    "numLogs", "nLogs", "hits", "events", "eventsCount", "value",
  ];

  for (const k of preferredKeys) {
    if (k in obj) {
      const n = toNumberSafe(obj[k]);
      if (n !== null) return n;
    }
  }

  for (const [k, v] of Object.entries(obj)) {
    const lk = k.toLowerCase();
    if (lk.includes("count") || lk.includes("total") || lk.includes("log")) {
      const n = toNumberSafe(v);
      if (n !== null) return n;
    }
  }

  for (const v of Object.values(obj)) {
    if (Array.isArray(v)) {
      for (const el of v) {
        const n = findCountDeep(el);
        if (n !== null) return n;
      }
    } else if (v && typeof v === "object") {
      const n = findCountDeep(v);
      if (n !== null) return n;
    }
  }

  return null;
};

const getCountFromRow = (x) => {
  const n =
    toNumberSafe(
      x?.count ?? x?.conta ?? x?.logsCount ?? x?.numLogs ?? x?.nLogs ??
        x?.total ?? x?.totale ?? x?.value ?? x?._id?.count ?? x?._id?.total
    ) ?? findCountDeep(x);

  return Number.isFinite(n) ? n : 0;
};

const safeToIso = (d) => {
  if (!d) return null;
  if (typeof d?.toISOString === "function") return d.toISOString();
  const asDate = d instanceof Date ? d : new Date(d);
  return Number.isNaN(asDate.getTime()) ? null : asDate.toISOString();
};

// matricola -> { critical, high }
const buildCounts = (criticalItems, highItems) => {
  const map = new Map();
  const bump = (items, field) =>
    items.forEach((x) => {
      if (!x?.matricola) return;
      const cur = map.get(x.matricola) ?? { critical: 0, high: 0 };
      cur[field] = getCountFromRow(x);
      map.set(x.matricola, cur);
    });
  bump(criticalItems, "critical");
  bump(highItems, "high");
  return map;
};

const useLecsEventCounts = (dateFrom, dateTo) => {
  const [isLoading, setIsLoading] = useState(false);
  const [counts, setCounts] = useState(() => new Map());
  const [error, setError] = useState("");

  const { startDateIso, endDateIso } = useMemo(
    () => ({ startDateIso: safeToIso(dateFrom), endDateIso: safeToIso(dateTo) }),
    [dateFrom, dateTo]
  );

  const refresh = useCallback(async () => {
    // hard-guard ISO (evita richieste con date invalide)
    if (!Number.isFinite(Date.parse(startDateIso)) || !Number.isFinite(Date.parse(endDateIso))) return;

    setIsLoading(true);
    setError("");
    setCounts(new Map());

    try {
      const query = (type) =>
        API.getCustomerHighLights({ queryType: "counts", type, startDate: startDateIso, endDate: endDateIso });
      const [resultHigh, resultCritical] = await Promise.all([query(0), query(1)]);
      setCounts(buildCounts(unwrapApiArray(resultCritical), unwrapApiArray(resultHigh)));
    } catch (err) {
      console.error("[useLecsEventCounts] fetch error:", err);
      setError("Unable to load events.");
    } finally {
      setIsLoading(false);
    }
  }, [startDateIso, endDateIso]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Seleziona il customer e apre la pagina log del livello richiesto nel periodo corrente.
  const goToEvents = useCallback(
    (level, matricola) => {
      // hard-guard: evita NaN/Invalid Date che in Firefox rompe dayjs.tz()
      if (!Number.isFinite(Date.parse(startDateIso)) || !Number.isFinite(Date.parse(endDateIso))) {
        console.error("[goToEvents] invalid dates:", { startDateIso, endDateIso });
        return;
      }
      if (level !== "high" && level !== "critical") return;

      API.postCurrentCustomer({ serial_number: matricola })
        .then(() => {
          const qs = `dateFrom=${encodeURIComponent(startDateIso)}&dateTo=${encodeURIComponent(endDateIso)}`;
          window.sessionStorage.setItem("selectedTab", level === "high" ? "High" : "Critical");
          window.location.href = `/reseller/myC/${level}?${qs}`;
        })
        .catch(() => {});
    },
    [startDateIso, endDateIso]
  );

  return { counts, isLoading, error, refresh, goToEvents };
};

export default useLecsEventCounts;
